/**
 * Tetrish - Sound Manager Service
 * Handles the game's audio playback and management
 */

// Import sound assets using Vite's asset importing
import clickSound from '/assets/sounds/ui/click.mp3';
import hoverSound from '/assets/sounds/ui/hover.mp3';
import pauseSound from '/assets/sounds/ui/pause.mp3';
import resumeSound from '/assets/sounds/ui/resume.mp3';
import moveSound from '/assets/sounds/game/move.mp3';
import rotateSound from '/assets/sounds/game/rotate.mp3';
import hardDropSound from '/assets/sounds/game/hard-drop.mp3';
import holdSound from '/assets/sounds/ui/click.mp3';
import clear1Sound from '/assets/sounds/ui/click.mp3';
import clear2Sound from '/assets/sounds/ui/click.mp3';
import clear3Sound from '/assets/sounds/ui/click.mp3';
import clear4Sound from '/assets/sounds/ui/click.mp3';
import levelUpSound from '/assets/sounds/game/level-up.mp3';
import gameStartSound from '/assets/sounds/game/game-start.mp3';
import gameOverSound from '/assets/sounds/game/game-over.mp3';
import bgMusicSound from '/assets/sounds/music/tetris-background.mp3';

export class SoundManager {
	constructor() {
		// Sound library
		this.sounds = {};

		// Settings
		this.volume = 0.5;
		this.musicEnabled = true;
		this.sfxEnabled = true;
		this.currentMusic = null;
		this.isGameRunning = false;

		// Make instance globally available for debugging
		window.soundManager = this;

		// Initialize
		this.init();
	}

	/**
	 * Initialize the sound manager
	 */
	init() {
		// Load settings from local storage
		this.loadSettings();

		// Register event listeners for settings changes
		window.addEventListener('settings-changed', (event) => {
			if (event.detail) {
				this.applySettings(event.detail);
			}
		});

		// Preload sounds (no auto-play)
		this.preloadSounds().then(success => {
			if (success) {
				console.log('Sounds preloaded');
			}
		});

		// Listen for game start events
		window.addEventListener('game-started', () => {
			console.log('Game started event received, playing background music');
			this.isGameRunning = true;
			if (this.musicEnabled) this.playSound('bgMusic');
		});

		// Listen for game end events
		window.addEventListener('game-ended', () => {
			console.log('Game ended event received, stopping background music');
			this.isGameRunning = false;
			this.stopMusic();
		});

		// Add a listener for first user interaction to handle autoplay restrictions
		const userInteractionHandler = () => {
			console.log('User interaction detected, trying to play background music');
			this.playSound('bgMusic');
			// Remove the event listeners after first interaction
			document.removeEventListener('click', userInteractionHandler);
			document.removeEventListener('keydown', userInteractionHandler);
		};

		document.addEventListener('click', userInteractionHandler);
		document.addEventListener('keydown', userInteractionHandler);
	}

	/**
	 * Preload game sounds
	 */
	async preloadSounds() {
		try {
			// Define audio files with imported assets
			const soundFiles = {
				// UI Sounds
				'click': clickSound,
				'hover': hoverSound,
				'pause': pauseSound,
				'resume': resumeSound,

				// Game Sounds
				'move': moveSound,
				'rotate': rotateSound,
				'hardDrop': hardDropSound,
				'hold': holdSound,
				'clear1': clear1Sound,
				'clear2': clear2Sound,
				'clear3': clear3Sound,
				'clear4': clear4Sound,
				'levelUp': levelUpSound,
				'gameStart': gameStartSound,
				'gameOver': gameOverSound,

				// Music Tracks
				'bgMusic': bgMusicSound,
			};

			// Create a dummy audio context for browsers that require user interaction
			const AudioContext = window.AudioContext || window.webkitAudioContext;
			const audioContext = new AudioContext();

			// Use a placeholder implementation when assets aren't available yet
			for (const [key, path] of Object.entries(soundFiles)) {
				this.sounds[key] = {
					path,
					audio: null,
					loaded: false
				};
			}

			// Actively preload the background music rather than waiting for it to be called
			console.log('Attempting to preload background music');
			await this.loadSound('bgMusic');

			console.log('Sound manager initialized');
			return true;
		} catch (error) {
			console.error('Error initializing sound manager:', error);
			return false;
		}
	}

	/**
	 * Load an individual sound file
	 */
	async loadSound(id) {
		if (!this.sounds[id]) return false;

		try {
			const sound = this.sounds[id];

			// Skip if already loaded
			if (sound.loaded) return true;

			// Log attempt to load sound
			console.log(`Attempting to load sound: ${id} from ${sound.path}`);

			// Check if file exists before trying to load it
			try {
				const response = await fetch(sound.path, { method: 'HEAD' });
				if (!response.ok) {
					console.error(`Sound file not found: ${sound.path} (Status: ${response.status})`);
					return false;
				}
			} catch (fetchError) {
				console.error(`Error checking sound file: ${sound.path}`, fetchError);
				// Continue anyway as some browsers might block HEAD requests
			}

			// Create audio element
			const audio = new Audio();
			audio.preload = 'auto';
			audio.src = sound.path;

			// Wait for load
			await new Promise((resolve, reject) => {
				audio.oncanplaythrough = resolve;
				audio.onerror = (e) => {
					console.error(`Error loading audio file ${sound.path}:`, e);
					reject(e);
				};

				// Timeout in case file doesn't exist or load takes too long
				const timeout = setTimeout(() => {
					audio.oncanplaythrough = null;
					reject(new Error(`Timeout loading sound: ${id}`));
				}, 5000);

				audio.oncanplaythrough = () => {
					clearTimeout(timeout);
					console.log(`Successfully loaded sound: ${id}`);
					resolve();
				};
			});

			// Update sound object
			sound.audio = audio;
			sound.loaded = true;

			return true;
		} catch (error) {
			console.warn(`Could not load sound: ${id}`, error);

			return false;
		}
	}

	/**
	 * Play a sound effect
	 */
	playSound(id) {
		console.log(`Attempting to play sound: ${id}`);

		// Skip if sound effects are disabled
		if (!this.sfxEnabled && id !== 'bgMusic') return;

		// Skip if music is disabled and this is music
		if (!this.musicEnabled && id === 'bgMusic') {
			console.log('Music is disabled. Not playing bgMusic.');
			return;
		}

		// Get the sound
		const sound = this.sounds[id];

		// Handle missing sound gracefully
		if (!sound) {
			console.warn(`Sound not found: ${id}`);
			return;
		}

		// Try to load the sound if not already loaded
		if (!sound.loaded) {
			console.log(`Sound ${id} not loaded yet. Attempting to load...`);
			this.loadSound(id).then(loaded => {
				if (loaded) {
					console.log(`Sound ${id} successfully loaded, now playing`);
					this.playSoundInternal(id);
				}
			});
			return;
		}

		this.playSoundInternal(id);
	}

	/**
	 * Internal helper for playing sounds
	 */
	playSoundInternal(id) {
		const sound = this.sounds[id];

		if (!sound || !sound.audio) return;

		try {
			// Handle background music differently
			if (id === 'bgMusic') {
				console.log('Setting up background music');
				this.currentMusic = sound.audio;
				sound.audio.loop = true;
				sound.audio.volume = this.volume * 0.7; // Music slightly quieter

				// Add error handling for music playback
				const playPromise = sound.audio.play();

				if (playPromise !== undefined) {
					playPromise.then(() => {
						console.log('Background music started successfully');
					}).catch(error => {
						console.error('Autoplay prevented:', error);

						// Store this music track to play when user interacts
						this._pendingMusic = id;

						// Add one-time click listener to start audio
						if (!this._hasAddedInteractionHandler) {
							this._hasAddedInteractionHandler = true;
							document.addEventListener('click', () => {
								if (this._pendingMusic && this.musicEnabled) {
									console.log('User interacted, trying to play music again');
									this.playSound(this._pendingMusic);
									this._pendingMusic = null;
								}
							}, { once: true });
						}
					});
				}
				return;
			}

			// For sound effects, clone the audio to allow overlapping sounds
			const audioClone = sound.audio.cloneNode(true);
			audioClone.volume = this.volume;

			// Release media resources once playback finishes to prevent memory leaks
			audioClone.onended = () => {
				audioClone.onended = null;
				audioClone.src = '';
			};

			const playPromise = audioClone.play();
			if (playPromise !== undefined) {
				playPromise.catch(error => {
					console.warn(`Error playing sound effect: ${id}`, error);
					audioClone.src = '';
				});
			}
		} catch (error) {
			console.warn(`Error playing sound: ${id}`, error);
		}
	}

	/**
	 * Stop background music
	 */
	stopMusic() {
		if (this.currentMusic) {
			this.currentMusic.pause();
			this.currentMusic.currentTime = 0;
		}
	}

	/**
	 * Pause background music
	 */
	pauseMusic() {
		if (this.currentMusic) {
			this.currentMusic.pause();
		}
	}

	/**
	 * Resume background music
	 */
	resumeMusic() {
		if (this.currentMusic && this.musicEnabled && this.isGameRunning) {
			this.currentMusic.play();
		}
	}

	/**
	 * Apply settings from settings object
	 */
	applySettings(settings) {
		if (settings.volume !== undefined) {
			this.volume = settings.volume / 100;

			// Update volume of currently playing music
			if (this.currentMusic) {
				this.currentMusic.volume = this.volume * 0.7;
			}
		}

		if (settings.music !== undefined) {
			this.musicEnabled = settings.music;
			if (this.musicEnabled && this.isGameRunning) {
				this.resumeMusic();
			} else {
				this.pauseMusic();
			}
		}

		if (settings.sfx !== undefined) {
			this.sfxEnabled = settings.sfx;
		}

		// Save settings
		this.saveSettings();
	}

	/**
	 * Save current settings to localStorage
	 */
	saveSettings() {
		try {
			const settings = {
				volume: Math.round(this.volume * 100),
				musicEnabled: this.musicEnabled,
				sfxEnabled: this.sfxEnabled
			};

			localStorage.setItem('tetrish-sound-settings', JSON.stringify(settings));
		} catch (error) {
			console.error('Error saving sound settings:', error);
		}
	}

	/**
	 * Load settings from localStorage
	 */
	loadSettings() {
		try {
			const storedSettings = localStorage.getItem('tetrish-sound-settings');

			if (storedSettings) {
				const settings = JSON.parse(storedSettings);

				this.volume = (settings.volume !== undefined) ? settings.volume / 100 : 0.5;
				this.musicEnabled = (settings.musicEnabled !== undefined) ? settings.musicEnabled : true;
				this.sfxEnabled = (settings.sfxEnabled !== undefined) ? settings.sfxEnabled : true;
			}
		} catch (error) {
			console.error('Error loading sound settings:', error);
		}
	}

	/**
	 * Test background music playback explicitly
	 */
	testPlayMusic() {
		console.log('Testing music playback');

		// First stop any current music
		this.stopMusic();

		// Try to play the music
		if (this.sounds['bgMusic'] && this.sounds['bgMusic'].loaded) {
			console.log('Playing already loaded bgMusic');
			this.playSound('bgMusic');
		} else {
			console.log('Loading and playing bgMusic');
			this.loadSound('bgMusic').then(success => {
				if (success) {
					this.playSound('bgMusic');
				}
			});
		}

		// Return the state for debugging
		return {
			musicEnabled: this.musicEnabled,
			currentMusic: this.currentMusic ? 'yes' : 'no',
			audioContext: window.AudioContext || window.webkitAudioContext ? 'available' : 'unavailable',
			volume: this.volume
		};
	}
}
